import { type DefaultOptions, MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { isUnauthorized } from "./api";
import { sessionExpiry } from "./session";

export interface QueryClientOptions {
  /**
   * What tells that the session ended while the page was open (default `isUnauthorized`: an
   * `ApiError` 401): no retry, and the browser signs in again, then comes back. `false` for an app
   * without a session to watch.
   */
  isUnauthorized?: ((error: unknown) => boolean) | false;
  /** The app's sign-in page, when it is not `/signin` (crowroom's `/login`, crowdex's `/sign-in`). */
  signinPage?: string;
  /** How many times other failures are tried again. Default 1. */
  retries?: number;
  /** The queries' defaults, over the shared ones (`retries`, no refetch when the window gets the focus back). */
  queries?: DefaultOptions["queries"];
}

/** The web app's query client, with the shared defaults. */
export function createQueryClient(options: QueryClientOptions = {}): QueryClient {
  const { isUnauthorized: unauthorized = isUnauthorized, retries = 1, signinPage, queries } = options;
  if (unauthorized === false)
    return new QueryClient({ defaultOptions: { queries: { retry: retries, refetchOnWindowFocus: false, ...queries } } });
  const expiry = sessionExpiry(unauthorized, retries, signinPage);
  return new QueryClient({
    queryCache: new QueryCache({ onError: expiry.onError }),
    mutationCache: new MutationCache({ onError: expiry.onError }),
    defaultOptions: { queries: { retry: expiry.retry, refetchOnWindowFocus: false, ...queries } },
  });
}
